// Dimension-driven track generator. Border-aware: both-side markings and
// start/finish stay INSIDE the border. Coordinates in turf pixels.
export function computeTrack(design, W, H) {
  const L = Math.max(0.1, design.dimensions.length);
  const Wm = Math.max(0.1, design.dimensions.width);
  const t = design.track || {};
  const m = t.markings || {};
  const colour = m.colour || "#f5f5f0";
  const prims = [];
  const lanes = Math.max(1, Math.min(5, Number(t.lanes) || (t.layout === "two_lane" ? 2 : 1)));
  const multi = lanes >= 2;
  const bw = design.border?.enabled ? (design.border.width_m || 0) : 0;
  const insetY = (bw / Wm) * H + (bw ? H * 0.05 : H * 0.02);
  const insetX = (bw / L) * W + (bw ? W * 0.01 : 0);

  // Lane dividers split the WIDTH (screen height) into `lanes` equal bands.
  if (multi) {
    for (let i = 1; i < lanes; i++) prims.push({ key: `div:${i}`, type: "rect", x: 0, y: (H * i) / lanes - 1, w: W, h: 2, fill: colour });
  }

  let rows;
  if (multi && m.lane_mode === "per_lane") rows = Array.from({ length: lanes }, (_, i) => (H * (i + 0.5)) / lanes);
  else if (m.position === "both_sides") rows = [insetY + H * 0.06, H - insetY - H * 0.06];
  else rows = [H * 0.5];

  const type = m.type || "none";
  const interval = m.interval || 1;
  // Physical metre-marker length scales with turf WIDTH (7.5%, clamped 0.25–0.50 m) — the "1 m" is
  // the interval along the LENGTH, never the marker's own size. Converted to px via the width scale.
  const tickH = markingLengthM(Wm) * (H / Wm);
  const numSize = Math.max(9, Math.min(H * 0.13, 22));
  // Centre numbered markers render as  — n —  (line, centred number, line).
  // Centre-only styling flag; both-side layout keeps its existing behaviour.
  const flankStyle = !multi && m.position !== "both_sides";
  const mlw = Math.max(2, Math.min(H * 0.03, 4)); // width-way line thickness (x extent)
  if (type !== "none") {
    for (let mm = interval; mm <= L + 1e-9; mm += interval) {
      const x = (mm / L) * W;
      if (x > W - 0.5) break;
      if (type === "numbers") {
        // NUMBERS: number only — no lines/ticks
        rows.forEach((ry, r) => prims.push({ key: `num:${mm}:${r}`, type: "text", x, y: ry, text: String(Math.round(mm)), fill: colour, size: numSize, anchor: "middle" }));
      } else if (flankStyle) {
        // CENTRE: metre line runs ACROSS THE WIDTH (vertical on screen), inset from edges
        const cy = H / 2;
        if (type === "lines") {
          // METRE LINES: short centred vertical marker only (no full-width span)
          const half = H * 0.18;
          prims.push({ key: `line:${mm}:c`, type: "rect", x: x - mlw / 2, y: cy - half, w: mlw, h: half * 2, fill: colour });
        } else {
          // LINES + Nº: width-way line split around the centred number, inset from edges
          const inset = H * 0.2;
          const top = inset, bottom = H - inset;
          const gap = Math.max(numSize * 0.75, 8);
          const th = cy - gap - top;
          const bh = bottom - (cy + gap);
          if (th > 1) prims.push({ key: `line:${mm}:top`, type: "rect", x: x - mlw / 2, y: top, w: mlw, h: th, fill: colour });
          if (bh > 1) prims.push({ key: `line:${mm}:bot`, type: "rect", x: x - mlw / 2, y: cy + gap, w: mlw, h: bh, fill: colour });
          prims.push({ key: `num:${mm}:0`, type: "text", x, y: cy, text: String(Math.round(mm)), fill: colour, size: numSize, anchor: "middle" });
        }
      } else {
        // Both Sides (unchanged): short tick + number beside for lines_numbers
        rows.forEach((ry, r) => {
          // Both-sides markers extend INWARD from the turf edge (inside any border); per-lane markers stay centred on the lane.
          const edge = !(multi && m.lane_mode === "per_lane");
          const y0 = edge ? (ry < H / 2 ? insetY : H - insetY - tickH) : ry - tickH / 2;
          prims.push({ key: `tick:${mm}:${r}`, type: "rect", x: x - 1, y: y0, w: 2, h: tickH, fill: colour });
          if (type === "lines_numbers") {
            let ny = ry < H / 2 ? y0 + tickH + numSize * 0.7 : y0 - numSize * 0.7;
            ny = Math.max(numSize, Math.min(H - numSize, ny));
            prims.push({ key: `num:${mm}:${r}`, type: "text", x, y: ny, text: String(Math.round(mm)), fill: colour, size: numSize, anchor: "middle" });
          }
        });
      }
    }
  }
  const lw = Math.max(2, W * 0.004);
  if (m.start) prims.push({ key: "start", type: "rect", x: insetX, y: insetY, w: lw, h: H - 2 * insetY, fill: colour });
  if (m.finish) prims.push({ key: "finish", type: "rect", x: W - insetX - lw, y: insetY, w: lw, h: H - 2 * insetY, fill: colour });
  if (m.start_finish_text) {
    // Per-lane START/FINISH text: sized to the lane band so it stays small and sits at the
    // start and end of EACH lane (1 lane => single centred label, just smaller than before).
    const bandH = H / lanes;
    const sfSize = Math.max(8, Math.min(bandH * 0.22, 24));
    const sfRows = multi
      ? Array.from({ length: lanes }, (_, i) => (H * (i + 0.5)) / lanes)
      : [H / 2];
    sfRows.forEach((ry, r) => {
      prims.push({ key: `sf:start:${r}`, type: "text", x: insetX + lw + W * 0.014, y: ry, text: "START", fill: colour, size: sfSize, anchor: "middle", rotate: 90 });
      prims.push({ key: `sf:finish:${r}`, type: "text", x: W - insetX - lw - W * 0.014, y: ry, text: "FINISH", fill: colour, size: sfSize, anchor: "middle", rotate: 90 });
    });
  }
  // Individually deleted generated elements (track.hidden) are simply omitted — nothing is regenerated or repositioned.
  const hidden = new Set(t.hidden || []);
  return prims.filter((p) => !hidden.has(p.key));
}

// Reusable: physical metre-marker length in metres for a given turf width (any dimensions).
export function markingLengthM(widthM) {
  return Math.min(0.5, Math.max(0.25, widthM * 0.075));
}
